import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API_BASE = 'http://localhost:8000/api';

const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', Accept: 'application/json' } };
};

const CATEGORY_CONFIG = {
    'Departments Without Managers': { color: '#dc2626', icon: '⚠️', route: '/company/departments' },
    'Vacant Manager Positions': { color: '#dc2626', icon: '🔴', route: '/company/positions' },
    'Employees Requiring Reassignment': { color: '#ea580c', icon: '👤', route: '/company/employees' },
    'Positions Exceeding Headcount': { color: '#ea580c', icon: '📊', route: '/company/positions' },
    'Broken Reporting Relationships': { color: '#dc2626', icon: '🔗', route: '/company/positions' },
    'Departments Requiring Profile Assignment': { color: '#d97706', icon: '📋', route: '/company/departments' },
    'Scheduled Changes': { color: '#2563eb', icon: '📅', route: '/company/scheduled-changes' },
};

const PendingActions = () => {
    const navigate = useNavigate();
    const companyId = localStorage.getItem('selectedCompanyId') || 1;

    const [groups, setGroups] = useState({});
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
    const [error, setError] = useState(null);
    const [expandedCategories, setExpandedCategories] = useState(new Set());

    const handleError = (err) => {
        if (err.response && (err.response.status === 401 || err.response.status === 403)) {
            localStorage.removeItem('token'); localStorage.removeItem('user'); navigate('/login'); return true;
        }
        return false;
    };

    useEffect(() => {
        const fetchActions = async () => {
            try {
                setLoading(true);
                const res = await axios.get(`${API_BASE}/companies/${companyId}/pending-actions`, getAuthHeaders());
                const data = res.data.data || res.data || {};
                setGroups(data);
                setTotalCount(res.data.total_count || Object.values(data).reduce((sum, arr) => sum + arr.length, 0));
                // Auto-expand all categories
                setExpandedCategories(new Set(Object.keys(data)));
                setError(null);
            } catch (err) {
                if (!handleError(err)) setError('Failed to load pending actions.');
            } finally {
                setLoading(false);
            }
        };
        fetchActions();
    }, [companyId]);

    const toggleCategory = (cat) => {
        setExpandedCategories(prev => {
            const next = new Set(prev);
            if (next.has(cat)) next.delete(cat); else next.add(cat);
            return next;
        });
    };

    const goToItem = (item) => {
        const config = CATEGORY_CONFIG[item._category];
        if (config) navigate(config.route);
    };

    const categories = Object.keys(groups).filter(k => groups[k] && groups[k].length > 0);

    // Severity counts
    const critical = categories.filter(c => {
        const cfg = CATEGORY_CONFIG[c];
        return cfg && cfg.color === '#dc2626';
    }).reduce((sum, c) => sum + groups[c].length, 0);

    const warnings = categories.filter(c => {
        const cfg = CATEGORY_CONFIG[c];
        return cfg && (cfg.color === '#ea580c' || cfg.color === '#d97706');
    }).reduce((sum, c) => sum + groups[c].length, 0);

    const info = categories.filter(c => {
        const cfg = CATEGORY_CONFIG[c];
        return cfg && cfg.color === '#2563eb';
    }).reduce((sum, c) => sum + groups[c].length, 0);

    // ── Styles ──
    const pageStyle = { padding: '24px 32px', backgroundColor: '#f1f5f9', minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' };
    const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' };
    const titleStyle = { fontSize: '1.5rem', fontWeight: '700', color: '#0f172a', margin: 0 };
    const cardsRow = { display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' };
    const card = (color) => ({ flex: '1', minWidth: '150px', backgroundColor: '#fff', borderRadius: '12px', padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', borderLeft: `4px solid ${color}` });
    const cardLabel = { fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '600', marginBottom: '4px' };
    const cardValue = { fontSize: '1.5rem', fontWeight: '700', color: '#0f172a' };

    if (initialLoading) return <div style={pageStyle}><p style={{ textAlign: 'center', color: '#64748b', marginTop: '60px' }}>Loading pending actions...</p></div>;
    if (error) return <div style={pageStyle}><p style={{ textAlign: 'center', color: '#dc2626', marginTop: '60px' }}>{error}</p></div>;

    return (
        <div style={pageStyle}>
            {/* Header */}
            <div style={headerStyle}>
                <div>
                    <h1 style={titleStyle}>Pending Actions</h1>
                    <p style={{ fontSize: '0.85rem', color: '#64748b', margin: '4px 0 0 0' }}>
                        {totalCount === 0 ? 'Nothing needs attention right now.' : `${totalCount} item${totalCount === 1 ? '' : 's'} need attention.`}
                    </p>
                </div>
            </div>

            {/* Summary Cards */}
            <div style={cardsRow}>
                <div style={card('#2563eb')}><div style={cardLabel}>Total</div><div style={cardValue}>{totalCount}</div></div>
                <div style={card('#dc2626')}><div style={cardLabel}>Critical</div><div style={cardValue}>{critical}</div></div>
                <div style={card('#d97706')}><div style={cardLabel}>Warnings</div><div style={cardValue}>{warnings}</div></div>
                <div style={card('#2563eb')}><div style={cardLabel}>Info</div><div style={cardValue}>{info}</div></div>
            </div>

            {/* All Good */}
            {categories.length === 0 && (
                <div style={{
                    backgroundColor: '#fff', borderRadius: '12px', padding: '48px', textAlign: 'center',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)', borderLeft: '4px solid #16a34a',
                }}>
                    <div style={{ fontSize: '2rem', marginBottom: '8px' }}>✅</div>
                    <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#166534' }}>Everything looks good</div>
                    <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '4px' }}>No pending actions require your attention.</div>
                </div>
            )}

            {/* Category Groups */}
            {categories.map(category => {
                const config = CATEGORY_CONFIG[category] || { color: '#64748b', icon: '📌', route: '/' };
                const items = groups[category] || [];
                const isExpanded = expandedCategories.has(category);

                return (
                    <div key={category} style={{
                        backgroundColor: '#fff', borderRadius: '12px', marginBottom: '12px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden',
                        borderLeft: `4px solid ${config.color}`,
                    }}>
                        {/* Category Header */}
                        <div
                            onClick={() => toggleCategory(category)}
                            style={{
                                padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                cursor: 'pointer', backgroundColor: '#f8fafc', borderBottom: isExpanded ? '1px solid #e2e8f0' : 'none',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ fontSize: '1rem' }}>{config.icon}</span>
                                <span style={{ fontWeight: '700', fontSize: '0.9rem', color: '#0f172a' }}>{category}</span>
                                <span style={{
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    minWidth: '22px', height: '22px', borderRadius: '999px', fontSize: '0.75rem', fontWeight: '700',
                                    backgroundColor: config.color, color: '#fff', padding: '0 6px',
                                }}>
                                    {items.length}
                                </span>
                            </div>
                            <span style={{ fontSize: '0.85rem', color: '#94a3b8', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▼</span>
                        </div>

                        {/* Items */}
                        {isExpanded && items.map((item, idx) => (
                            <div
                                key={idx}
                                onClick={() => goToItem({ ...item, _category: category })}
                                style={{
                                    padding: '12px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    borderBottom: idx < items.length - 1 ? '1px solid #f1f5f9' : 'none',
                                    cursor: 'pointer',
                                }}
                                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8fafc'}
                                onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}
                            >
                                <span style={{ fontSize: '0.875rem', color: '#334155' }}>{item.description}</span>
                                <span style={{ fontSize: '0.75rem', fontWeight: '600', color: '#2563eb', whiteSpace: 'nowrap', marginLeft: '16px' }}>View →</span>
                            </div>
                        ))}
                    </div>
                );
            })}
        </div>
    );
};

export default PendingActions;